import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import SettingSidebar from "../../components/VerticalLayout/SettingSidebar";
import DeleteModal from "../../components/Common/DeleteModal";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { ToastContainer } from "react-toastify";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { map } from "lodash";

import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import SafeField from "helpers/SafeField";

import {
  addTransStatusType,
  getTransStatusType,
  updateTransStatusType,
  deleteTransStatusType,
} from "../../store/actions";

const TransactionStatusType = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { transStatusTypes, loading } = useSelector(
    (state) => state.ObjectReducer
  );

  const [transStatusType, setTransStatusType] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isRight, setIsRight] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // =============================
  // Load Status Types
  // =============================
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    dispatch(getTransStatusType(userId));
  }, [dispatch]);

  // =============================
  // Canvas Controls
  // =============================
  const openEditCanvas = (data) => {
    setTransStatusType({
      id: data.id,
      transStatusId: data.transStatusId,
      agentId: data.agentId,
      title: data.title,
      description: data.description,
      isActive: data.isActive,
    });
    setIsEdit(true);
    setIsRight(true);
  };

  const closeCanvas = () => {
    setTransStatusType({});
    setIsEdit(false);
    setIsRight(false);
  };

  // =============================
  // Add Submit
  // =============================
  const handleSubmit = (values, { resetForm }) => {
    dispatch(addTransStatusType(values, history));
    resetForm();
  };

  // =============================
  // Delete
  // =============================
  const onClickDelete = (item) => {
    setTransStatusType(item);
    setDeleteModal(true);
  };

  const handleDelete = () => {
    if (transStatusType?.id) {
      dispatch(deleteTransStatusType(transStatusType));
      setDeleteModal(false);
    }
  };

  // =============================
  // Update Submit
  // =============================
  const handleUpdate = (values) => {
    const updateData = {
      id: values.id,
      transStatusId: values.transStatusId,
      agentId: values.agentId,
      title: values.title,
      description: values.description,
      isActive: values.isActive,
    };

    dispatch(updateTransStatusType(updateData));
    closeCanvas();
  };

  return (
    <React.Fragment>
      {loading && <div className="loading">Loading…</div>}
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
       
        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={t("General Settings")}
                breadcrumbItem={t("Transaction Status Type")}
              />
            </Col>
          </Row>

          <Row>
           
            <Col xl="12">
              {/* ================= Add Form ================= */}
              <Formik
                initialValues={{ title: "" }}
                validationSchema={Yup.object({
                  title: Yup.string().required("This is required"),
                })}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Card>
                      <CardBody>
                        
                        <Row>
                          <Col sm="3">
                            <FormGroup className="mb-3">
                              <Label>{t("Title *")}</Label>

                              <SafeField
                                name="title"
                                type="text"
                                onChange={handleChange}
                                className={
                                  "form-control" +
                                  (errors.title && touched.title
                                    ? " is-invalid"
                                    : "")
                                }
                              />

                              <ErrorMessage
                                name="title"
                                component="div"
                                className="invalid-feedback"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="2">
                            <Button
                              type="submit"
                              color="primary"
                              style={{ marginTop: 31 }}
                            >
                              Add
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* ================= History ================= */}
                    <Card>
                      <CardBody>
                        <h5 className="card-title">History</h5>

                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {map(transStatusTypes, (item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.title}</td>
                                  <td>
                                    <ActionEdit
                                      onClick={() => openEditCanvas(item)}
                                    />
                                    <ActionDelete
                                      onClick={() => onClickDelete(item)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>

          {/* ================= Edit Offcanvas ================= */}
          <Offcanvas isOpen={isRight} direction="end" toggle={closeCanvas}>
            <OffcanvasHeader toggle={closeCanvas}>
              Update
            </OffcanvasHeader>

            <OffcanvasBody>
              <Formik
                enableReinitialize
                initialValues={{
                  id: transStatusType?.id,
                  transStatusId: transStatusType?.transStatusId,
                  agentId: transStatusType?.agentId,
                  title: transStatusType?.title || "",
                  description: transStatusType?.description,
                  isActive: transStatusType?.isActive,
                }}
                validationSchema={Yup.object({
                  title: Yup.string().required("This is required"),
                })}
                onSubmit={handleUpdate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <FormGroup className="mb-3">
                      <Label>{t("Title *")}</Label>

                      <SafeField
                        name="title"
                        type="text"
                        onChange={handleChange}
                        className={
                          "form-control" +
                          (errors.title && touched.title
                            ? " is-invalid"
                            : "")
                        }
                      />

                      <ErrorMessage
                        name="title"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FormGroup>

                    <Button type="submit" color="primary">
                      Update
                    </Button>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TransactionStatusType;
